import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { Trans, useTranslation } from 'react-i18next'

export default function Register() {
  const { signUp } = useAuth()
  const { t } = useTranslation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')

    if (password !== confirm) {
      setError(t('register.errorPasswordMatch'))
      return
    }

    if (password.length < 6) {
      setError(t('register.errorPasswordLength'))
      return
    }

    setLoading(true)
    const { error: signUpError } = await signUp(email, password)

    if (signUpError) {
      setError(signUpError.message)
      setLoading(false)
    } else {
      setSuccess(true)
    }
  }

  if (success) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 text-center">
            <span className="text-5xl">📬</span>
            <h2 className="mt-4 text-xl font-bold text-gray-900">{t('register.checkEmailTitle')}</h2>
            <p className="mt-2 text-sm text-gray-500 leading-relaxed">
              <Trans
                i18nKey="register.checkEmailBody"
                values={{ email }}
                components={{ strong: <span className="font-medium text-gray-700" /> }}
              />
            </p>
            <Link
              to="/login"
              className="mt-6 inline-block w-full rounded-lg bg-oasis-500 px-6 py-3 text-sm font-semibold text-white hover:bg-oasis-700 transition text-center"
            >
              {t('register.goToLogin')}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
      <div className="w-full max-w-md">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full bg-oasis-50 border-2 border-oasis-200 flex items-center justify-center mx-auto">
              <span className="text-oasis-700 font-black text-2xl leading-none">O</span>
            </div>
            <h1 className="mt-3 text-2xl font-bold text-gray-900">{t('register.createAccount')}</h1>
            <p className="mt-1 text-sm text-gray-500">{t('register.subtitle')}</p>
          </div>

          {/* Error banner */}
          {error && (
            <div className="mb-5 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              <span className="mt-0.5 shrink-0">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="email">
                {t('register.emailAddress')}
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-oasis-500 focus:outline-none focus:ring-2 focus:ring-oasis-200 transition"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="password">
                {t('register.password')}
              </label>
              <input
                id="password"
                type="password"
                required
                autoComplete="new-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-oasis-500 focus:outline-none focus:ring-2 focus:ring-oasis-200 transition"
                placeholder={t('register.passwordMinHint')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="confirm">
                {t('register.confirmPassword')}
              </label>
              <input
                id="confirm"
                type="password"
                required
                autoComplete="new-password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                className={`w-full rounded-lg border px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 transition ${
                  confirm && confirm !== password
                    ? 'border-red-400 focus:border-red-400 focus:ring-red-200'
                    : 'border-gray-300 focus:border-oasis-500 focus:ring-oasis-200'
                }`}
                placeholder="••••••••"
              />
              {confirm && confirm !== password && (
                <p className="mt-1 text-xs text-red-600">{t('register.passwordsNoMatch')}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-oasis-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-oasis-700 focus:outline-none focus:ring-2 focus:ring-oasis-300 disabled:opacity-60 disabled:cursor-not-allowed transition"
            >
              {loading ? t('register.creatingAccount') : t('register.createAccountBtn')}
            </button>
          </form>

          {/* Footer link */}
          <p className="mt-6 text-center text-sm text-gray-500">
            {t('register.haveAccount')}{' '}
            <Link to="/login" className="font-medium text-oasis-500 hover:text-oasis-700 transition">
              {t('register.signIn')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
